@import 'tailwindcss';

@source '../../../../../../index.html';
@source '../../../../../**/*.{js,ts,html}';
@source '../../../../../../.storybook/**/*.{js,ts,mjs}';

@theme {
  --color-primary: var(--color-primary);
  --color-secondary: var(--color-secondary);
  --color-background: var(--color-background);
  --color-text: var(--color-text);
  /* Tailwind radii: cards 12px, inputs 8px, pills full */
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 12px;
  --radius-2xl: 12px;
  --radius-3xl: 12px;
  --radius-4xl: 12px;
  --radius-full: 9999px;
}

/**
 * App theme tokens (Material 3): palette + semantic roles.
 * Override --color-primary / --color-secondary via build env and applyTenantCssVariables.
 */
:root {
  /* Brand palette anchors */
  --app-sky-900: #0d3d66;
  --app-sky-800: #155a9e;
  /* Sky blue (primary action) */
  --app-sky-700: #1a6fbf;
  --app-sky-600: #2d82d3;
  --app-sky-100: #d6ebfc;
  --app-sky-50: #f0f7fd;

  /* Ochre (tower) */
  --app-ochre-700: #6b4e2e;
  --app-ochre-500: #a67c52;
  --app-ochre-100: #f0e8dc;

  /* Mist grays */
  --app-mist-600: #4a5159;
  --app-mist-500: #7d8694;
  --app-mist-200: #d8dee6;
  --app-mist-100: #eef1f5;

  /* Institutional gold */
  --app-gold-700: #8a6b1f;
  --app-gold-500: #c9a227;
  --app-gold-100: #faf3dd;

  /* Flag green (integrity / city) */
  --app-accent-green-800: #0a4a32;
  --app-accent-green-700: #0d6b47;
  --app-accent-green-600: #15803d;
  --app-accent-green-100: #d9f3e5;

  /* Standard ink: text and outlines */
  --app-ink: #0a0a0a;
  --app-ink-muted: #2d3238;

  --app-white: #ffffff;

  /* Default primary/secondary (overridable via tenant / applyTenantCssVariables) */
  --color-primary: var(--app-sky-900);
  --color-secondary: var(--app-accent-green-700);

  --md-sys-color-primary: var(--color-primary);
  --md-sys-color-on-primary: var(--app-white);
  --md-sys-color-primary-container: color-mix(in srgb, var(--color-primary) 16%, var(--app-sky-50));
  --md-sys-color-on-primary-container: var(--app-sky-900);

  --md-sys-color-secondary: var(--color-secondary);
  --md-sys-color-on-secondary: var(--app-white);
  --md-sys-color-secondary-container: var(--app-accent-green-100);
  --md-sys-color-on-secondary-container: var(--app-accent-green-800);

  --md-sys-color-tertiary: var(--app-gold-500);
  --md-sys-color-on-tertiary: var(--app-ink);
  --md-sys-color-tertiary-container: var(--app-gold-100);
  --md-sys-color-on-tertiary-container: var(--app-gold-700);

  /* M3 surfaces (tonal steps, aligned to Material 3 roles) */
  --md-sys-color-surface: var(--app-sky-50);
  --md-sys-color-surface-dim: color-mix(in srgb, var(--app-sky-600) 6%, var(--app-white));
  --md-sys-color-surface-container-low: color-mix(in srgb, var(--app-sky-600) 4%, var(--app-white));
  --md-sys-color-surface-container: color-mix(in srgb, var(--app-sky-600) 8%, var(--app-white));
  --md-sys-color-surface-container-high: color-mix(in srgb, var(--app-sky-600) 10%, var(--app-white));
  --md-sys-color-surface-container-highest: var(--app-mist-100);

  --md-sys-color-on-surface: var(--app-ink);
  --md-sys-color-on-surface-variant: var(--app-ink-muted);
  --md-sys-color-outline: var(--app-mist-500);
  --md-sys-color-outline-variant: var(--app-mist-200);

  --md-sys-color-error: #ba1a1a;
  --md-sys-color-on-error: var(--app-white);
  --md-sys-color-error-container: #ffdad6;
  --md-sys-color-on-error-container: #410002;

  --md-elevation-1: 0 1px 2px rgba(10, 10, 10, 0.06), 0 1px 3px 1px rgba(26, 111, 191, 0.05);
  --md-elevation-2: 0 1px 2px rgba(10, 10, 10, 0.05), 0 2px 6px 2px rgba(26, 111, 191, 0.06);
  --md-elevation-3: 0 4px 8px 3px rgba(10, 10, 10, 0.07), 0 1px 3px rgba(26, 111, 191, 0.06);

  /* Component corners: cards 12px, inputs 8px, pills full */
  --md-shape-corner-extra-large: 12px;
  --md-shape-corner-large: 12px;
  --md-shape-corner-medium: 8px;
  --md-shape-corner-full: 9999px;

  --md-state-hover-opacity: 0.08;
  --md-state-pressed-opacity: 0.12;
  --md-state-focus-opacity: 0.12;

  /* M3 motion (Android default duration / easing) */
  --md-motion-duration-short1: 50ms;
  --md-motion-duration-short2: 200ms;
  --md-motion-duration-short3: 150ms;
  --md-motion-duration-short4: 250ms;
  --md-motion-duration-medium1: 300ms;
  --md-motion-duration-medium2: 400ms;
  --md-motion-easing-standard: cubic-bezier(0.2, 0, 0, 1);
  --md-motion-easing-emphasized: cubic-bezier(0.2, 0, 0, 1);
  --md-motion-easing-emphasized-decelerate: cubic-bezier(0.05, 0.7, 0.1, 1);
  --md-motion-easing-emphasized-accelerate: cubic-bezier(0.3, 0, 0.8, 0.15);
}

@keyframes md-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes md-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes md-enter-fade-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes md-enter-scale {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes md-error-enter {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes md-skeleton-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@keyframes md-enter-slide-from-end {
  from {
    opacity: 0;
    transform: translateX(24px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes md-exit-slide-to-start {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-24px);
  }
}

@keyframes md-enter-slide-from-start {
  from {
    opacity: 0;
    transform: translateX(-24px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes md-exit-slide-to-end {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(24px);
  }
}

@keyframes md-btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.md-page-enter-forward {
  animation: md-enter-slide-from-end var(--md-motion-duration-medium2) var(--md-motion-easing-emphasized-decelerate) both;
}

.md-page-enter-back {
  animation: md-enter-slide-from-start var(--md-motion-duration-medium2) var(--md-motion-easing-emphasized-decelerate) both;
}

.md-surface-card {
  position: relative;
  overflow: hidden;
  margin-bottom: 0.75rem;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-shape-corner-large);
  box-shadow: var(--md-elevation-1);
  transition:
    box-shadow var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    transform var(--md-motion-duration-short4) var(--md-motion-easing-emphasized),
    border-color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

@media (hover: hover) {
  .md-surface-card:hover {
    border-color: color-mix(in srgb, var(--md-sys-color-outline) 65%, var(--md-sys-color-outline-variant));
    box-shadow: var(--md-elevation-2);
  }
}

.md-surface-card:active {
  transform: scale(0.995);
}

.md-skeleton {
  border-radius: var(--md-shape-corner-medium);
  background: linear-gradient(
    90deg,
    var(--md-sys-color-surface-container) 0%,
    var(--md-sys-color-surface-container-high) 50%,
    var(--md-sys-color-surface-container) 100%
  );
  background-size: 200% 100%;
  animation: md-skeleton-shimmer 1.4s var(--md-motion-easing-standard) infinite;
}

.md-skeleton--title {
  height: 1.75rem;
  width: 55%;
  margin-bottom: 1rem;
}

.md-skeleton--block {
  height: 6rem;
  width: 100%;
  margin-bottom: 1rem;
}

.md-skeleton--line {
  height: 1rem;
  width: 100%;
  margin-bottom: 0.625rem;
}

.md-skeleton--line:last-child {
  width: 72%;
}

.md-error-banner {
  animation: md-error-enter var(--md-motion-duration-medium1) var(--md-motion-easing-emphasized-decelerate) both;
}

.md-field--stacked {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.md-field__label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: var(--md-sys-color-on-surface);
}

.md-field__required {
  color: var(--md-sys-color-error);
  font-weight: 600;
}

.md-field--invalid .md-field__input,
.md-field__input--invalid {
  border-color: var(--md-sys-color-error);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--md-sys-color-error) 35%, transparent);
}

.md-field--invalid .md-field__label {
  color: var(--md-sys-color-error);
}

.md-field__hint {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  line-height: 1.25rem;
}

.md-field__hint--error {
  color: var(--md-sys-color-error);
  animation: md-error-enter var(--md-motion-duration-medium1) var(--md-motion-easing-emphasized-decelerate) both;
}

.input-md {
  color: var(--md-sys-color-on-surface);
  background: var(--md-sys-color-surface-container-highest);
  border-color: var(--md-sys-color-outline);
}

.input-md::placeholder {
  color: var(--md-sys-color-on-surface-variant);
  opacity: 1;
}

.input-md:focus {
  border-color: var(--md-sys-color-primary);
  background: var(--md-sys-color-surface-container-high);
  --tw-ring-color: color-mix(in srgb, var(--md-sys-color-primary) 28%, transparent);
}

.shell-select-chips-chip__remove {
  touch-action: manipulation;
}

.md-form-error-summary {
  background: var(--md-sys-color-error-container);
}

dialog[open]:not(.md-snackbar) {
  animation: md-enter-scale var(--md-motion-duration-medium2) var(--md-motion-easing-emphasized-decelerate) both;
}

.shell-modal:not([open]) {
  display: none !important;
}

.shell-modal[open] {
  position: fixed;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: none;
  height: 100vh;
  max-height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  border: none;
  border-radius: 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  overflow: hidden;
}

@media (min-width: 1024px) {
  .shell-modal[open] {
    width: min(42rem, calc(100vw - 3rem));
    max-width: 42rem;
    height: auto;
    max-height: min(90vh, calc(100vh - 3rem));
    max-height: min(90dvh, calc(100dvh - 3rem));
    margin: auto;
    border-radius: var(--md-shape-corner-extra-large);
    box-shadow: var(--md-elevation-3);
  }
}

.shell-modal__body {
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 1.25rem;
  padding-top: max(1.25rem, env(safe-area-inset-top, 0px));
  padding-bottom: max(1.25rem, env(safe-area-inset-bottom, 0px));
}

.shell-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.shell-modal__actions {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  padding-bottom: max(0.25rem, env(safe-area-inset-bottom, 0px));
}

.shell-modal__header-text {
  min-width: 0;
  flex: 1;
}

.shell-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: var(--md-sys-color-on-surface);
}

.shell-modal__subtitle {
  margin: 0.375rem 0 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--md-sys-color-on-surface-variant);
}

.md-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin: -0.25rem -0.25rem 0 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  transition:
    background-color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.md-btn-icon:hover {
  background: color-mix(in srgb, var(--md-sys-color-on-surface) 8%, transparent);
  color: var(--md-sys-color-on-surface);
}

.md-btn-icon:active {
  background: color-mix(in srgb, var(--md-sys-color-on-surface) 12%, transparent);
}

.md-btn-icon:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

.md-btn-icon__glyph {
  width: 1.375rem;
  height: 1.375rem;
}

dialog[open]::backdrop,
.shell-modal::backdrop {
  background: color-mix(in srgb, var(--app-ink) 42%, transparent);
  animation: md-backdrop-in var(--md-motion-duration-short4) var(--md-motion-easing-standard) both;
}

.shell-modal--stack::backdrop {
  background: color-mix(in srgb, var(--app-ink) 55%, transparent);
}

@media (min-width: 1024px) {
  :root {
    --shell-bottom-nav-height: 0px;
  }
}

@keyframes md-backdrop-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.md-btn-filled {
  min-height: 2.5rem;
  padding: 0.625rem 1.5rem;
  border: none;
  border-radius: var(--md-shape-corner-medium);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.00625em;
  cursor: pointer;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  transition: background-color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.md-btn-filled:hover {
  background: color-mix(in srgb, var(--md-sys-color-on-primary) 8%, var(--md-sys-color-primary));
}

.md-btn-filled:active {
  background: color-mix(in srgb, var(--md-sys-color-on-primary) 12%, var(--md-sys-color-primary));
}

.md-btn-filled:disabled {
  background: color-mix(in srgb, var(--md-sys-color-primary) 45%, var(--md-sys-color-surface-container-highest));
  color: var(--md-sys-color-on-surface);
  cursor: not-allowed;
}

.md-btn-filled--busy,
.md-btn-tonal-error--busy {
  position: relative;
  color: transparent !important;
  pointer-events: none;
}

.md-btn-filled--busy::after,
.md-btn-tonal-error--busy::after {
  content: '';
  position: absolute;
  inset: 0;
  margin: auto;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid color-mix(in srgb, var(--md-sys-color-on-primary) 35%, transparent);
  border-top-color: var(--md-sys-color-on-primary);
  border-radius: 50%;
  animation: md-btn-spin 0.75s linear infinite;
}

.md-btn-tonal-error--busy::after {
  border-top-color: var(--md-sys-color-on-error);
}

.shell-header-elevated {
  box-shadow: var(--md-elevation-2) !important;
}

.md-btn-outlined {
  min-height: 2.5rem;
  padding: 0.625rem 1.5rem;
  border: 1px solid var(--md-sys-color-outline);
  border-radius: var(--md-shape-corner-medium);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  color: var(--md-sys-color-primary);
  transition:
    background-color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    border-color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.md-btn-outlined:hover {
  background: color-mix(in srgb, var(--md-sys-color-primary) 6%, transparent);
}

.md-btn-tonal-error {
  min-height: 2.5rem;
  padding: 0.625rem 1.5rem;
  border: none;
  border-radius: var(--md-shape-corner-medium);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  background: var(--md-sys-color-error);
  color: var(--md-sys-color-on-error);
  transition: opacity var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.md-btn-tonal-error:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login-overlay {
  animation: md-fade-in var(--md-motion-duration-medium2) var(--md-motion-easing-standard) both;
}

.shell-sync-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: color-mix(in srgb, var(--md-sys-color-scrim) 45%, transparent);
  animation: md-fade-in var(--md-motion-duration-short2) var(--md-motion-easing-standard) both;
}

.shell-sync-overlay__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-radius: var(--md-shape-corner-large);
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-3);
}

.shell-sync-overlay__spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid var(--md-sys-color-outline-variant);
  border-top-color: var(--md-sys-color-primary);
  border-radius: 50%;
  animation: shell-sync-spin 0.85s linear infinite;
}

@keyframes shell-sync-spin {
  to {
    transform: rotate(360deg);
  }
}

.shell-sync-upload-progress__track {
  height: 0.25rem;
  overflow: hidden;
  border-radius: var(--md-shape-corner-full);
  background: var(--md-sys-color-surface-container-highest);
}

.shell-sync-upload-progress__fill {
  height: 100%;
  border-radius: inherit;
  background: var(--md-sys-color-primary);
  transition: width var(--md-motion-duration-short3) var(--md-motion-easing-standard);
}

.shell-storage-bar {
  display: flex;
  height: 0.875rem;
  overflow: hidden;
  border-radius: var(--md-shape-corner-full);
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--app-white);
  --shell-storage-sqlite: var(--app-sky-600);
  --shell-storage-images: var(--app-accent-green-600);
  --shell-storage-signatures: var(--app-ochre-500);
  --shell-storage-available: var(--app-mist-100);
}

.shell-storage-bar__segment {
  height: 100%;
  min-width: 0;
  box-shadow: inset -1px 0 0 color-mix(in srgb, var(--app-white) 55%, transparent);
}

.shell-storage-bar__segment:last-child {
  box-shadow: none;
}

.shell-storage-bar__swatch {
  display: inline-block;
  width: 0.75rem;
  height: 0.75rem;
  flex-shrink: 0;
  border-radius: var(--md-shape-corner-extra-small);
  border: 1px solid color-mix(in srgb, var(--app-ink) 8%, transparent);
}

.shell-storage-bar__swatch--sqlite {
  background: var(--shell-storage-sqlite);
}

.shell-storage-bar__swatch--images {
  background: var(--shell-storage-images);
}

.shell-storage-bar__swatch--signatures {
  background: var(--shell-storage-signatures);
}

.shell-storage-bar__swatch--available {
  background: var(--shell-storage-available);
}

.shell-storage-bar__segment--sqlite {
  background: var(--shell-storage-sqlite);
}

.shell-storage-bar__segment--images {
  background: var(--shell-storage-images);
}

.shell-storage-bar__segment--signatures {
  background: var(--shell-storage-signatures);
}

.shell-storage-bar__segment--available {
  background: repeating-linear-gradient(
    -45deg,
    var(--shell-storage-available),
    var(--shell-storage-available) 5px,
    var(--app-white) 5px,
    var(--app-white) 10px
  );
}

.login-card {
  margin-bottom: 0.75rem;
  border: 1px solid var(--md-sys-color-outline-variant);
  animation: md-enter-scale var(--md-motion-duration-medium2) var(--md-motion-easing-emphasized-decelerate) both;
  animation-delay: 80ms;
}

.shell-brand-logo {
  view-transition-name: shell-brand-logo;
}

::view-transition-old(shell-brand-logo),
::view-transition-new(shell-brand-logo) {
  animation: md-enter-scale var(--md-motion-duration-medium2) var(--md-motion-easing-emphasized-decelerate) both;
}

.shell-offline-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.125rem;
  padding: 0.125rem 0.5rem;
  border-radius: var(--md-shape-corner-full);
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.2;
  color: var(--md-sys-color-on-error-container);
  background: var(--md-sys-color-error-container);
}

.shell-offline-chip__dot {
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--md-sys-color-error);
}

.shell-route-announcer {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.shell-main-host {
  view-transition-name: shell-main-content;
}

html[data-shell-nav='forward'] ::view-transition-old(shell-main-content) {
  animation: md-exit-slide-to-start var(--md-motion-duration-short3) var(--md-motion-easing-emphasized-accelerate) forwards;
}

html[data-shell-nav='forward'] ::view-transition-new(shell-main-content) {
  animation: md-enter-slide-from-end var(--md-motion-duration-medium1) var(--md-motion-easing-emphasized-decelerate) forwards;
}

html[data-shell-nav='back'] ::view-transition-old(shell-main-content) {
  animation: md-exit-slide-to-end var(--md-motion-duration-short3) var(--md-motion-easing-emphasized-accelerate) forwards;
}

html[data-shell-nav='back'] ::view-transition-new(shell-main-content) {
  animation: md-enter-slide-from-start var(--md-motion-duration-medium1) var(--md-motion-easing-emphasized-decelerate) forwards;
}

::view-transition-old(shell-main-content) {
  animation: md-fade-out var(--md-motion-duration-short3) var(--md-motion-easing-emphasized-accelerate) forwards;
}

::view-transition-new(shell-main-content) {
  animation: md-enter-fade-up var(--md-motion-duration-medium1) var(--md-motion-easing-emphasized-decelerate) forwards;
}

.shell-app-root {
  view-transition-name: shell-app-root;
}

::view-transition-old(shell-app-root),
::view-transition-new(shell-app-root) {
  animation-duration: var(--md-motion-duration-medium2);
}

/* Scroll: contain overscroll; closer to native views */
main#main,
.layout-main-scroll {
  overscroll-behavior-y: contain;
}

.pull-refresh-indicator {
  display: flex;
  justify-content: center;
  height: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity var(--md-motion-duration-short4);
}

.pull-refresh-indicator--pulling,
.pull-refresh-indicator--busy {
  opacity: 1;
}

.pull-refresh-indicator__icon {
  width: 1.75rem;
  height: 1.75rem;
  margin-top: calc(var(--pull-offset, 0px) - 1.75rem);
  border: 2px solid color-mix(in srgb, var(--md-sys-color-outline) 55%, transparent);
  border-top-color: var(--md-sys-color-primary);
  border-radius: 50%;
  transform: rotate(calc(var(--pull-progress, 0) * 180deg));
}

.pull-refresh-indicator--busy .pull-refresh-indicator__icon {
  animation: md-btn-spin 0.75s linear infinite;
}

/* Android-style bottom nav: shell footer, safe-area aware */
.layout-bottom-nav {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

body {
  margin: 0;
  min-height: 100vh;
  min-height: 100dvh;
  --shell-bottom-nav-height: calc(4.25rem + env(safe-area-inset-bottom, 0px));
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    Arial,
    sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.01em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  background-color: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
}

.md-sys-ripple {
  position: relative;
  overflow: hidden;
}

.md-sys-ripple::after {
  content: '';
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--md-motion-duration-short2) var(--md-motion-easing-standard);
  pointer-events: none;
  border-radius: inherit;
}

.md-sys-ripple:hover::after {
  opacity: var(--md-state-hover-opacity);
}

.md-sys-ripple:active::after {
  opacity: var(--md-state-pressed-opacity);
}

/**
 * Shell: Navigation Rail / Navigation Bar (M3 destination).
 * Active icon uses primary-container; label-medium typography.
 * Bottom bar: animated top pill indicator on destination change.
 */
.shell-rail-nav a,
.shell-bottom-nav a {
  position: relative;
}

.shell-rail-nav a .shell-nav-dest-icon-shell,
.shell-bottom-nav a .shell-nav-dest-icon-shell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--md-shape-corner-medium);
  transition:
    background-color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    transform var(--md-motion-duration-short4) var(--md-motion-easing-emphasized);
}

.shell-rail-nav a[aria-current='page'] .shell-nav-dest-icon-shell,
.shell-bottom-nav a[aria-current='page'] .shell-nav-dest-icon-shell {
  background-color: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  transform: scale(1.04);
}

.shell-rail-nav a:not([aria-current='page']) .shell-nav-dest-icon-shell,
.shell-bottom-nav a:not([aria-current='page']) .shell-nav-dest-icon-shell {
  color: var(--md-sys-color-on-surface-variant);
}

.shell-rail-nav a .shell-nav-dest-label,
.shell-bottom-nav a .shell-nav-dest-label {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  letter-spacing: 0.00625em;
  transition: color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.shell-rail-nav a[aria-current='page'] .shell-nav-dest-label,
.shell-bottom-nav a[aria-current='page'] .shell-nav-dest-label {
  color: var(--md-sys-color-on-primary-container);
}

.shell-rail-nav a:not([aria-current='page']) .shell-nav-dest-label,
.shell-bottom-nav a:not([aria-current='page']) .shell-nav-dest-label {
  color: var(--md-sys-color-on-surface-variant);
}

.shell-bottom-nav a.md-sys-ripple::after {
  z-index: 0;
}

.shell-bottom-nav a::before {
  content: '';
  position: absolute;
  z-index: 1;
  top: 0;
  left: 50%;
  width: 1.5rem;
  height: 3px;
  border-radius: var(--md-shape-corner-medium);
  background-color: var(--md-sys-color-primary);
  transform: translateX(-50%) scaleX(0.2);
  opacity: 0;
  transition:
    opacity var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    transform var(--md-motion-duration-short4) var(--md-motion-easing-emphasized);
  pointer-events: none;
}

.shell-bottom-nav a[aria-current='page']::before {
  transform: translateX(-50%) scaleX(1);
  opacity: 1;
}

.layout-rail-nav {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.shell-rail-nav a.md-sys-ripple::after {
  z-index: 0;
}

.shell-rail-nav a::before {
  content: '';
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 0;
  width: 3px;
  height: 1.5rem;
  border-radius: var(--md-shape-corner-medium);
  background-color: var(--md-sys-color-primary);
  transform: translateY(-50%) scaleY(0.2);
  opacity: 0;
  transition:
    opacity var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    transform var(--md-motion-duration-short4) var(--md-motion-easing-emphasized);
  pointer-events: none;
}

.shell-rail-nav a[aria-current='page']::before {
  transform: translateY(-50%) scaleY(1);
  opacity: 1;
}

.shell-pending-sync-badge {
  position: absolute;
  top: -0.2rem;
  right: -0.35rem;
  z-index: 2;
  display: flex;
  min-width: 1.125rem;
  height: 1.125rem;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
  border-radius: 9999px;
  background-color: var(--md-sys-color-error);
  color: var(--md-sys-color-on-error);
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--md-sys-color-shadow, #000) 28%, transparent);
  pointer-events: none;
}

/* Snackbar (Material / Android); DomToastNotificationAdapter — dialog top layer so it sits above shell modals */
.md-snackbar {
  position: fixed;
  inset: auto;
  top: auto;
  left: max(16px, env(safe-area-inset-left));
  right: max(16px, env(safe-area-inset-right));
  bottom: calc(max(16px, env(safe-area-inset-bottom, 0px)) + var(--shell-bottom-nav-height, 0px));
  width: auto;
  height: auto;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  overflow: visible;
  display: flex;
  justify-content: center;
  pointer-events: none;
  transform: translateY(calc(100% + 24px));
  opacity: 0;
  transition:
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s ease;
}

.md-snackbar::backdrop {
  background: transparent;
  pointer-events: none;
}

.md-snackbar.md-snackbar--visible {
  transform: translateY(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .md-snackbar {
    transition: opacity 0.15s ease;
  }

  .md-snackbar:not(.md-snackbar--visible) {
    transform: translateY(0);
  }

  .md-page-enter-forward,
  .md-page-enter-back,
  .login-card,
  .login-overlay,
  .md-error-banner,
  .md-skeleton,
  .shell-rail-nav a .shell-nav-dest-icon-shell,
  .shell-bottom-nav a .shell-nav-dest-icon-shell,
  .shell-bottom-nav a::before,
  .input-md {
    transition: none;
  }

  .md-surface-card {
    animation: none !important;
    transition: none !important;
    transform: none !important;
  }

  ::view-transition-old(shell-main-content),
  ::view-transition-new(shell-main-content),
  ::view-transition-old(shell-app-root),
  ::view-transition-new(shell-app-root),
  ::view-transition-old(shell-brand-logo),
  ::view-transition-new(shell-brand-logo) {
    animation: none !important;
    opacity: 1 !important;
  }
}

.md-snackbar__surface {
  pointer-events: auto;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  max-width: min(560px, 100%);
  padding: 0.875rem 1rem;
  border-radius: var(--md-shape-corner-medium);
  box-shadow: var(--md-elevation-3);
  background: var(--md-sys-color-surface-container-highest);
  color: var(--md-sys-color-on-surface);
  border: 1px solid var(--md-sys-color-outline-variant);
}

.md-snackbar__surface--success {
  border-color: color-mix(in srgb, var(--md-sys-color-secondary) 38%, var(--md-sys-color-outline-variant));
}

.md-snackbar__surface--error {
  border-color: color-mix(in srgb, var(--md-sys-color-error) 45%, var(--md-sys-color-outline-variant));
  background: var(--md-sys-color-error-container);
  color: var(--md-sys-color-on-error-container);
}

.md-snackbar__surface--warning {
  border-color: color-mix(in srgb, var(--app-ochre-500) 45%, var(--md-sys-color-outline-variant));
}

.md-snackbar__surface--info {
  border-color: color-mix(in srgb, var(--md-sys-color-primary) 35%, var(--md-sys-color-outline-variant));
}

.md-snackbar__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--md-sys-color-primary);
}

.md-snackbar__surface--success .md-snackbar__icon {
  color: var(--md-sys-color-secondary);
}

.md-snackbar__surface--error .md-snackbar__icon {
  color: var(--md-sys-color-error);
}

.md-snackbar__surface--warning .md-snackbar__icon {
  color: var(--app-ochre-700);
}

.md-snackbar__text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.4;
  font-weight: 400;
}

.shell-header-pending-chip {
  display: inline-flex;
  align-items: center;
  margin-top: 0.125rem;
  padding: 0.125rem 0.5rem;
  border-radius: var(--md-shape-corner-full);
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
}

.shell-more-sheet {
  position: fixed;
  inset: auto 0 0 0;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: none;
  max-height: min(70vh, calc(100vh - 4rem));
  max-height: min(70dvh, calc(100dvh - 4rem));
  border: none;
  border-radius: var(--md-shape-corner-extra-large) var(--md-shape-corner-extra-large) 0 0;
  background: transparent;
  overflow: visible;
}

.shell-more-sheet::backdrop {
  background: color-mix(in srgb, var(--app-ink) 42%, transparent);
  animation: md-backdrop-in var(--md-motion-duration-short4) var(--md-motion-easing-standard) both;
}

.shell-more-sheet__body {
  padding: 1rem 1rem max(1rem, env(safe-area-inset-bottom));
  background: var(--md-sys-color-surface-container-high);
  border-radius: inherit;
  box-shadow: var(--md-elevation-3);
}

.shell-more-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.shell-more-sheet__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--md-sys-color-on-surface);
}

.shell-more-sheet__list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.shell-more-sheet__link {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-height: 3.25rem;
  padding: 0.625rem 0.75rem;
  border-radius: var(--md-shape-corner-medium);
  text-decoration: none;
  color: var(--md-sys-color-on-surface);
}

.shell-more-sheet__link--active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.shell-more-sheet__link .shell-nav-dest-icon-shell {
  position: relative;
  flex-shrink: 0;
}

.md-snackbar__action {
  margin-left: auto;
  flex-shrink: 0;
  align-self: center;
  padding: 0.375rem 0.75rem;
  border-radius: var(--md-shape-corner-medium);
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  color: var(--md-sys-color-primary);
  background: color-mix(in srgb, var(--md-sys-color-primary) 10%, transparent);
}

.shell-form-stepper {
  margin-bottom: 0.25rem;
}

.shell-form-scroll-section {
  padding-top: 1.25rem;
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.shell-form-scroll-section:first-of-type {
  padding-top: 0;
  border-top: none;
}

.shell-form-scroll-section__title {
  margin: 0 0 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--md-sys-color-on-surface-variant);
}

.shell-form-stepper__list {
  display: flex;
  gap: 0.375rem;
}

.shell-form-stepper__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.25rem;
  border-radius: var(--md-shape-corner-medium);
  color: var(--md-sys-color-on-surface-variant);
}

.shell-form-stepper__item--active {
  color: var(--md-sys-color-primary);
  background: var(--md-sys-color-primary-container);
}

.shell-form-stepper__item--done {
  color: var(--md-sys-color-on-primary-container);
}

.shell-form-stepper__dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--md-sys-color-surface-container-highest);
}

.shell-form-stepper__item--active .shell-form-stepper__dot {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.shell-form-stepper__item--done .shell-form-stepper__dot {
  background: var(--md-sys-color-secondary);
  color: var(--md-sys-color-on-secondary);
}

.shell-form-stepper__label {
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
}

.shell-filter-chip {
  min-height: 2.25rem;
  padding: 0.375rem 0.875rem;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-shape-corner-full);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--md-sys-color-on-surface-variant);
  background: var(--md-sys-color-surface-container);
  transition:
    background-color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    border-color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.shell-filter-chip--active {
  border-color: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
}

.shell-record-list-item {
  animation: shell-record-list-in var(--md-motion-duration-medium1) var(--md-motion-easing-emphasized-decelerate) both;
  animation-delay: calc(var(--shell-stagger-index, 0) * 40ms);
}

.shell-record-list-item .md-surface-card {
  margin-bottom: 0;
}

.shell-record-card {
  display: flex;
  min-height: 5.5rem;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem 0.875rem 1.125rem;
  border-left-width: 3px;
  border-left-style: solid;
}

.shell-record-card--pending {
  border-left-color: var(--md-sys-color-outline);
}

.shell-record-card--synced {
  border-left-color: var(--md-sys-color-primary);
}

.shell-record-card--failed {
  border-left-color: var(--md-sys-color-error);
}

.shell-record-card__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.375rem;
}

.shell-record-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.shell-record-card__headline {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--md-sys-color-on-surface);
}

.shell-record-card__subline {
  margin: 0;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: var(--md-sys-color-on-surface-variant);
}

.shell-record-card__detail-text {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--md-sys-color-on-surface-variant);
}

.shell-record-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.shell-record-card__badge {
  display: inline-flex;
  align-items: center;
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 22%, var(--md-sys-color-outline-variant));
  border-radius: var(--md-shape-corner-full);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--md-sys-color-primary-container) 80%, var(--md-sys-color-surface-container-high)) 0%,
    var(--md-sys-color-primary-container) 100%
  );
  padding: 0.25rem 0.625rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--md-sys-color-on-primary-container);
  box-shadow: 0 1px 0 color-mix(in srgb, var(--md-sys-color-primary) 8%, transparent);
}

.shell-record-card__context {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.shell-record-card__context-badge {
  display: inline-flex;
  max-width: 100%;
  align-items: baseline;
  gap: 0.35rem;
  border: 1px solid color-mix(in srgb, var(--md-sys-color-secondary) 25%, var(--md-sys-color-outline-variant));
  border-radius: var(--md-shape-corner-full);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--md-sys-color-secondary-container) 85%, var(--md-sys-color-surface-container-high)) 0%,
    var(--md-sys-color-secondary-container) 100%
  );
  padding: 0.25rem 0.625rem;
  font-size: 0.6875rem;
  line-height: 1.25;
  color: var(--md-sys-color-on-secondary-container);
  box-shadow: 0 1px 0 color-mix(in srgb, var(--md-sys-color-secondary) 8%, transparent);
}

.shell-record-card__context-label {
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  opacity: 0.82;
}

.shell-record-card__context-value {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell-sync-badge {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  border-radius: var(--md-shape-corner-full);
  padding: 0.2rem 0.625rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.shell-sync-badge--pending {
  border: 1px solid color-mix(in srgb, var(--app-gold-500) 45%, var(--md-sys-color-outline-variant));
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--app-gold-100) 90%, var(--app-white)) 0%,
    var(--md-sys-color-tertiary-container) 100%
  );
  color: var(--app-gold-700);
  box-shadow: 0 1px 2px color-mix(in srgb, var(--app-gold-500) 18%, transparent);
}

.shell-sync-badge--synced {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.shell-sync-badge--failed {
  background: var(--md-sys-color-error-container);
  color: var(--md-sys-color-on-error-container);
}

.shell-sync-badge--success {
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
}

.shell-record-card__error {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--md-sys-color-error);
}

.shell-record-card__error--warning {
  color: var(--md-sys-color-on-tertiary-container);
}

.shell-record-card__footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-top: 1px solid var(--md-sys-color-outline-variant);
  padding-top: 0.625rem;
  font-size: 0.75rem;
  color: var(--md-sys-color-on-surface-variant);
}

.shell-record-card__date {
  flex-shrink: 0;
  font-weight: 500;
}

.shell-record-card__ref {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.6875rem;
  opacity: 0.85;
}

.shell-record-card__chevron {
  margin-left: auto;
}

.shell-record-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.shell-record-card__actions .md-btn-filled,
.shell-record-card__actions .md-btn-outlined,
.shell-record-card__actions .md-btn-text {
  flex: 1 1 auto;
  min-width: 7rem;
  font-size: 0.875rem;
}

button.shell-record-card:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

@keyframes shell-record-list-in {
  from {
    opacity: 0;
    transform: translateY(0.375rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .shell-record-list-item {
    animation: none;
  }
}

.shell-signature-fullscreen {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  border: none;
  background: var(--md-sys-color-surface);
}

.shell-signature-fullscreen::backdrop {
  background: color-mix(in srgb, var(--app-ink) 45%, transparent);
}

.shell-signature-fullscreen__body {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  padding: max(1rem, env(safe-area-inset-top, 0px)) 1rem max(1rem, env(safe-area-inset-bottom, 0px));
}

.shell-signature-fullscreen__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.shell-signature-fullscreen__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--md-sys-color-on-surface);
}

.shell-signature-fullscreen__canvas {
  display: block;
  width: 100%;
  flex: 1;
  min-height: 12rem;
  border-radius: var(--md-shape-corner-medium);
  border: 1px dashed var(--md-sys-color-outline-variant);
  background: var(--app-white);
}

/* WCAG 2.1 — visible focus (contrast vs institutional ink) */
:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

button:focus:not(:focus-visible),
a:focus:not(:focus-visible) {
  outline: none;
}

/* iOS Safari hace auto-zoom al enfocar controles con fuente <16px; solo aplica en iOS */
@supports (-webkit-touch-callout: none) {
  input,
  select,
  textarea {
    font-size: max(16px, 1em);
  }
}
